import { useEffect, useMemo, useState, type JSX } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Pill } from '@/components/ui/pill';
import { channelLabel } from '@/components/ui/tag';
import {
  DataTable,
  DataTableCell,
  DataTableHeader,
  DataTableHeaderCell,
  DataTableRow,
} from '@/components/ui/data-table';
import { PlatformIcon } from '@/components/PlatformIcon';
import { OutreacherPicker } from '@/components/money/OutreacherPicker';
import { useBulkAddOutreachAccounts } from '@/hooks/useOutreachAccounts';
import { parseAccountPaste, type PastedAccountRow } from '@/lib/outreachAccounts';
import type { OutreachAccountBulkResult } from '@/api/types';
import { textareaClass } from './AccountFormDialog';

const EXAMPLE = 'instagram,ether.dm1,password1\ntiktok,ether_tt,password2,login@example.com,warm-up week 2';

/**
 * "Paste many": one account per line for one outreacher, previewed as a table
 * (passwords masked) before anything is saved. After saving, each row shows
 * what happened — handles already in the vault are reported, not fatal.
 */
export function BulkAddDialog({
  open,
  onOpenChange,
  fixedInviter,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fixedInviter?: { publicId: string; name: string };
}): JSX.Element {
  const bulk = useBulkAddOutreachAccounts();
  const [text, setText] = useState('');
  const [inviterId, setInviterId] = useState('');
  const [results, setResults] = useState<Map<number, OutreachAccountBulkResult> | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setText('');
    setInviterId('');
    setResults(null);
    setError(null);
  }, [open]);

  const rows = useMemo(() => parseAccountPaste(text), [text]);
  const ready = rows.filter((r) => !r.error);
  const target = fixedInviter?.publicId ?? inviterId;

  async function save(): Promise<void> {
    setError(null);
    if (!target) return setError('Pick the outreacher these accounts belong to.');
    try {
      const res = await bulk.mutateAsync({
        inviterId: target,
        rows: ready.map((r) => ({
          platform: r.platform!,
          username: r.username,
          ...(r.password ? { password: r.password } : {}),
          ...(r.email ? { email: r.email } : {}),
          ...(r.notes ? { notes: r.notes } : {}),
        })),
      });
      // Results are indexed by position in what we sent; map back to lines.
      const byLine = new Map<number, OutreachAccountBulkResult>();
      res.results.forEach((r) => {
        const row = ready[r.index];
        if (row) byLine.set(row.line, r);
      });
      setResults(byLine);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  const saved = results !== null;
  const createdCount = saved ? [...results.values()].filter((r) => r.status === 'created').length : 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !bulk.isPending && onOpenChange(o)}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Paste many accounts</DialogTitle>
          <DialogDescription>
            One per line: <code className="font-mono text-xs">platform,username,password[,email][,notes]</code>. Tabs work
            too (paste from a sheet), and are the way to go when a password has a comma in it.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3">
          {fixedInviter ? (
            <p className="text-sm">
              For <span className="font-semibold">{fixedInviter.name}</span>
            </p>
          ) : (
            <div className="grid gap-1">
              <Label htmlFor="oa-bulk-inviter">Outreacher</Label>
              <OutreacherPicker id="oa-bulk-inviter" value={inviterId} onChange={(id) => setInviterId(id)} disabled={saved} />
            </div>
          )}
          {!saved && (
            <div className="grid gap-1">
              <Label htmlFor="oa-bulk-text">Accounts</Label>
              <textarea
                id="oa-bulk-text"
                rows={6}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={EXAMPLE}
                spellCheck={false}
                autoComplete="off"
                className={`${textareaClass} font-mono text-xs`}
              />
            </div>
          )}

          {rows.length > 0 && (
            <div className="max-h-72 overflow-y-auto">
              <DataTable aria-label="Accounts to add" stickyHeader={false}>
                <DataTableHeader>
                  <tr>
                    <DataTableHeaderCell>Line</DataTableHeaderCell>
                    <DataTableHeaderCell>Platform</DataTableHeaderCell>
                    <DataTableHeaderCell>Username</DataTableHeaderCell>
                    <DataTableHeaderCell>Password</DataTableHeaderCell>
                    <DataTableHeaderCell>Email</DataTableHeaderCell>
                    <DataTableHeaderCell>Notes</DataTableHeaderCell>
                    <DataTableHeaderCell>{saved ? 'Result' : 'Check'}</DataTableHeaderCell>
                  </tr>
                </DataTableHeader>
                <tbody>
                  {rows.map((r) => (
                    <PreviewRow key={r.line} row={r} result={results?.get(r.line)} saved={saved} />
                  ))}
                </tbody>
              </DataTable>
            </div>
          )}

          {saved && (
            <p className="text-sm" role="status">
              Added {createdCount} of {rows.length}.
            </p>
          )}
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          {saved ? (
            <Button type="button" onClick={() => onOpenChange(false)}>
              Done
            </Button>
          ) : (
            <>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={bulk.isPending}>
                Cancel
              </Button>
              <Button type="button" onClick={() => void save()} disabled={bulk.isPending || ready.length === 0}>
                {bulk.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                {ready.length === 1 ? 'Save 1 account' : `Save ${ready.length} accounts`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PreviewRow({
  row,
  result,
  saved,
}: {
  row: PastedAccountRow;
  result: OutreachAccountBulkResult | undefined;
  saved: boolean;
}): JSX.Element {
  let outcome: JSX.Element;
  if (row.error) outcome = <Pill tone="rejected">{row.error}</Pill>;
  else if (!saved) outcome = <Pill tone="info">Ready</Pill>;
  else if (!result) outcome = <Pill tone="muted">Not sent</Pill>;
  else if (result.status === 'created') outcome = <Pill tone="success">Added</Pill>;
  else outcome = <Pill tone={result.status === 'duplicate' ? 'pending' : 'rejected'}>{result.message}</Pill>;

  return (
    <DataTableRow state={row.error ? 'terminal' : 'default'}>
      <DataTableCell muted>{row.line}</DataTableCell>
      <DataTableCell data-label="Platform">
        {row.platform ? (
          <span className="inline-flex items-center gap-1.5">
            <PlatformIcon platform={row.platform} size={12} /> {channelLabel(row.platform)}
          </span>
        ) : (
          '—'
        )}
      </DataTableCell>
      <DataTableCell anchor data-label="Username">
        {row.username ? `@${row.username}` : '—'}
      </DataTableCell>
      <DataTableCell muted data-label="Password">
        {row.password ? '••••••••' : '—'}
      </DataTableCell>
      <DataTableCell muted data-label="Email">
        {row.email || '—'}
      </DataTableCell>
      <DataTableCell muted data-label="Notes" className="max-w-[12rem] truncate">
        {row.notes || '—'}
      </DataTableCell>
      <DataTableCell data-label="Result">{outcome}</DataTableCell>
    </DataTableRow>
  );
}
